import dayjs from "dayjs"
import { TFunction } from "i18next"

import { InstanceFormQuestion } from "../store/evaluations-types"
import { AnswerDisplay } from "../ui/evaluations/fill-form-dialog/answer-comparison"

// Date-only values arrive as "YYYY-MM-DD" or "YYYY-MM-DDT00:00:00"; split the
// string rather than parse it so the calendar day never shifts by timezone.
export function formatPrintDate(value: string | null | undefined): string {
	if (!value) return "—"
	const parts = value.split("T")[0].split("-")
	if (parts.length !== 3) return value
	return `${parts[1]}/${parts[2]}/${parts[0]}`
}

// Timestamps from the API (acknowledgement times) are GMT. Some arrive
// without a "Z", which the browser would otherwise read as local time.
export function parseGmt(value: string | Date): Date {
	if (value instanceof Date) return value
	const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value)
	return new Date(hasZone ? value : `${value}Z`)
}

function zonedParts(
	date: Date,
	timeZone: string | null | undefined
): Record<string, string> {
	const options: Intl.DateTimeFormatOptions = {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
		timeZoneName: "short",
	}
	let formatter: Intl.DateTimeFormat
	try {
		formatter = new Intl.DateTimeFormat("en-US", {
			...options,
			timeZone: timeZone ?? undefined,
		})
	} catch {
		// Unknown zone name — the viewer's own zone is still correct, just
		// not the site's.
		formatter = new Intl.DateTimeFormat("en-US", options)
	}
	return Object.fromEntries(
		formatter.formatToParts(date).map((part) => [part.type, part.value])
	)
}

// "MM/DD/YYYY h:mm AM EDT" in the location's zone (the viewer's when none is
// given). The zone is printed because a paper record can't be hovered.
export function formatPrintDateTime(
	value: string | Date | null | undefined,
	timeZone?: string | null
): string {
	if (!value) return "—"
	const date = parseGmt(value)
	if (Number.isNaN(date.getTime())) return String(value)
	const p = zonedParts(date, timeZone)
	return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute} ${p.dayPeriod} ${p.timeZoneName}`
}

// The calendar day of a GMT timestamp, in the location's zone.
export function formatPrintTimestampDate(
	value: string | null | undefined,
	timeZone?: string | null
): string {
	if (!value) return "—"
	// A bare date has no time to shift.
	if (!value.includes("T")) return formatPrintDate(value)
	const date = parseGmt(value)
	if (Number.isNaN(date.getTime())) return value
	const p = zonedParts(date, timeZone)
	return `${p.month}/${p.day}/${p.year}`
}

// Plain-text rendering of an answer for paper: chips joined, Yes/No
// translated, dates as MM/DD/YYYY. Null means "no answer".
export function formatPrintAnswer(
	question: InstanceFormQuestion,
	answer: AnswerDisplay,
	t: TFunction
): string | null {
	if (answer.kind === "empty") return null
	if (answer.kind === "chips") return (answer.chips ?? []).join(", ")
	const text = answer.text ?? ""
	if (question.typeCode === "TrueFalse") {
		return text === "Yes" ? t("evaluations_yes") : t("evaluations_no")
	}
	if (question.typeCode === "Date") return formatPrintDate(text)
	if (question.typeCode === "DateTime") {
		// An answer, not a server timestamp — printed as the person entered it.
		const date = dayjs(text)
		return date.isValid() ? date.format("MM/DD/YYYY h:mm A") : text
	}
	return text
}
